import React, { useState, useEffect, useMemo } from 'react';
import { suivisAPI, apprenantsAPI, statsAPI, configAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import FormSelect from '../components/FormSelect';
import Icon from '../components/Icon';
import ModalSuiviMultiNiveaux from '../components/ModalSuiviMultiNiveaux';
import { format } from 'date-fns';
import useDebounce from '../hooks/useDebounce';

const Suivi = () => {
  const { isFormateur } = useAuth();
  const [suiviStats, setSuiviStats] = useState(null);
  const [suivis, setSuivis] = useState([]);
  const [apprenants, setApprenants] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSuiviModal, setShowSuiviModal] = useState(false);
  const [showMultiNiveauxModal, setShowMultiNiveauxModal] = useState(false);
  const [selectedApprenant, setSelectedApprenant] = useState(null);
  const [selectedMultiNiveauApprenant, setSelectedMultiNiveauApprenant] = useState(null);
  const [suiviForm, setSuiviForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    type: '',
    note: 0,
    appreciation: '',
    commentaire: ''
  });
  const [rating, setRating] = useState(0);
  const [filterLevel, setFilterLevel] = useState('ALL');
  const [sortOption, setSortOption] = useState('NAME');
  const [loadingTable, setLoadingTable] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState('');
  const [detailsApprenants, setDetailsApprenants] = useState([]);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [search, setSearch] = useState('');
  const [modalPage, setModalPage] = useState(1);
  const modalPerPage = 10;

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 15;

  const debouncedFilter = useDebounce(filterLevel, 250);
  const debouncedSort = useDebounce(sortOption, 250);
  const debouncedSearch = useDebounce(search, 250);
  useEffect(() => {
    fetchData();
  }, []);

  // ── buildNiveauxFor : lit directement depuis apprenant.suivisMultiNiveaux ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const buildNiveauxFor = (apprenant) => {
    const niveaux = {};
    (apprenant.suivisMultiNiveaux || []).forEach(n => {
      if (
        !niveaux[n.niveau] ||
        new Date(n.dateContact) > new Date(niveaux[n.niveau].dateContact)
      ) {
        niveaux[n.niveau] = n;
      }
    });
    return niveaux;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getLastNiveau = (apprenant, niveaux) => {
    if (apprenant && apprenant.dernierNiveauSuivi) return apprenant.dernierNiveauSuivi;
    if (niveaux['M+6']) return 'M+6';
    if (niveaux['M+3']) return 'M+3';
    if (niveaux['M+0']) return 'M+0';
    return null;
  };

  const formatDateShort = (d) => {
    if (!d) return '';
    try {
      return format(new Date(d), 'dd/MM/yyyy');
    } catch (e) {
      return d;
    }
  };

  const getNiveauDate = (niveaux, level) => {
    const entry = niveaux[level];
    if (!entry) return '';
    return entry.dateContact || entry.dateCreation || entry.date || '';
  };

  const levelPriority = (lvl) => {
    if (!lvl) return 0;
    if (lvl === 'M+6') return 3;
    if (lvl === 'M+3') return 2;
    if (lvl === 'M+0') return 1;
    return 0;
  };

  // ── Liste filtrée/triée complète ──
  const displayedApprenants = useMemo(() => {
    const searchLower = debouncedSearch.trim().toLowerCase();

    return apprenants
      .filter(apprenant => {
        const niveaux = buildNiveauxFor(apprenant);
        const last = getLastNiveau(apprenant, niveaux);
        if (debouncedFilter === 'ALL') return true;
        if (debouncedFilter === 'NONE') return !last;
        return last === debouncedFilter;
      })
      .filter(apprenant => {
        if (!searchLower) return true;
        return (
          apprenant.nom?.toLowerCase().includes(searchLower) ||
          apprenant.prenom?.toLowerCase().includes(searchLower) ||
          apprenant.entreprise?.toLowerCase().includes(searchLower) ||
          apprenant.email?.toLowerCase().includes(searchLower)
        );
      })
      .sort((a, b) => {
        if (debouncedSort === 'NAME') return (a.nom || '').localeCompare(b.nom || '');
        const na = levelPriority(getLastNiveau(a, buildNiveauxFor(a)));
        const nb = levelPriority(getLastNiveau(b, buildNiveauxFor(b)));
        if (debouncedSort === 'NIVEAU_DESC') return nb - na;
        return na - nb;
      });
  }, [apprenants, debouncedFilter, debouncedSort, debouncedSearch, buildNiveauxFor, getLastNiveau]);

  // ── Pagination calculée depuis displayedApprenants ──
  const totalPages = Math.ceil(displayedApprenants.length / perPage);
  const paginatedApprenants = displayedApprenants.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  // ── Historique ──
  const historique = useMemo(() => {
    return [...suivis]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 50);
  }, [suivis]);

  useEffect(() => {
    setLoadingTable(true);
    setCurrentPage(1); // reset page sur changement filtre/tri
    const t = setTimeout(() => setLoadingTable(false), 180);
    return () => clearTimeout(t);
  }, [debouncedFilter, debouncedSort, debouncedSearch, apprenants.length]);

  const fetchData = async () => {
    try {
      const [suiviResponse, statsResponse, apprenantsResponse, configResponse] = await Promise.all([
        suivisAPI.getAll(),
        statsAPI.getSuivi(),
        apprenantsAPI.getAll({ limit: 1000 }),
        configAPI.get()
      ]);
      setSuivis(suiviResponse.data.suivis || []);
      setSuiviStats(statsResponse.data);
      setApprenants(apprenantsResponse.data.apprenants || []);
      setConfig(configResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleKpiClick = (type) => {
    setActiveFilter(type);
    setModalPage(1);
    switch (type) {
      case 'SUIVIS_OK':
        setDetailsTitle('Suivis effectués');
        setDetailsApprenants(getApprenantsAvecSuiviOK());
        break;
      case 'EN_ATTENTE':
        setDetailsTitle('Suivis en attente');
        setDetailsApprenants(getApprenantsEnAttente());
        break;
      case 'EN_RETARD':
        setDetailsTitle('Suivis en retard');
        setDetailsApprenants(getApprenantsEnRetard());
        break;
      default:
        return;
    }
    setShowDetailsModal(true);
  };

  const getApprenantsAvecSuiviOK = () => {
    if (!config) return [];
    const positifs = config.statutsInsertion?.positif || [];
    return apprenants.filter(a => {
      if (!a.dateInsertion) return false;
      if (!positifs.includes(a.statutInsertion)) return false;
      return suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
    });
  };

  const getApprenantsEnAttente = () => {
    if (!config) return [];
    const delaiSuivi = config.delaiSuivi || 90;
    const positifs = config.statutsInsertion?.positif || [];
    const maintenant = new Date();
    return apprenants.filter(a => {
      if (!a.dateInsertion) return false;
      if (!positifs.includes(a.statutInsertion)) return false;
      const dateInsertion = new Date(a.dateInsertion);
      const dateSuiviAttendu = new Date(dateInsertion);
      dateSuiviAttendu.setDate(dateSuiviAttendu.getDate() + delaiSuivi);
      const aUnSuivi = suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
      if (aUnSuivi) return false;
      return maintenant < dateSuiviAttendu;
    });
  };

  const getApprenantsEnRetard = () => {
    if (!config) return [];
    const delaiSuivi = config.delaiSuivi || 90;
    const positifs = config.statutsInsertion?.positif || [];
    const maintenant = new Date();
    return apprenants.filter(a => {
      if (!a.dateInsertion) return false;
      if (!positifs.includes(a.statutInsertion)) return false;
      const dateInsertion = new Date(a.dateInsertion);
      const dateSuiviAttendu = new Date(dateInsertion);
      dateSuiviAttendu.setDate(dateSuiviAttendu.getDate() + delaiSuivi);
      const aUnSuivi = suivis.some(s => s.apprenantId?._id === a._id || s.apprenantId === a._id);
      if (aUnSuivi) return false;
      return maintenant >= dateSuiviAttendu;
    });
  };

  const openSuiviModal = (apprenantId) => {
    const apprenant = apprenants.find(a => a._id === apprenantId);
    if (!apprenant) return;
    setSelectedApprenant(apprenant);
    setSuiviForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      type: '',
      note: 0,
      appreciation: '',
      commentaire: ''
    });
    setRating(0);
    setShowSuiviModal(true);
  };

  const openMultiNiveauxModal = (apprenantId) => {
    const apprenant = apprenants.find(a => a._id === apprenantId);
    if (!apprenant) return;
    setSelectedMultiNiveauApprenant(apprenant);
    setShowMultiNiveauxModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormateur) {
      toast.error('Pas de droits');
      return;
    }
    try {
      await suivisAPI.create({
        apprenantId: selectedApprenant._id,
        ...suiviForm,
        note: rating
      });
      toast.success('Suivi enregistré !');
      setShowSuiviModal(false);
      await fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const getApprenantsAContacter = () => {
    if (!config) return [];
    const delaiSuivi = config.delaiSuivi || 90;
    const positifs = config.statutsInsertion?.positif || [];
    const maintenant = new Date();
    return apprenants.filter(a => {
      if (!a.dateInsertion) return false;
      if (!positifs.includes(a.statutInsertion)) return false;
      const dateInsertion = new Date(a.dateInsertion);
      const jours = Math.floor((maintenant - dateInsertion) / (1000 * 60 * 60 * 24));
      const dernierSuivi = suivis
        .filter(s => s.apprenantId?._id === a._id || s.apprenantId === a._id)
        .sort((x, y) => new Date(y.date) - new Date(x.date))[0];
      if (dernierSuivi) return false;
      return jours >= delaiSuivi;
    });
  };

  const exportCSV = () => {
    const headers = ['Nom', 'Email', 'Entreprise', 'DernierNiveau', 'M+0 Date', 'M+3 Date', 'M+6 Date'];
    const rows = displayedApprenants.map(apprenant => {
      const niveaux = buildNiveauxFor(apprenant);
      return [
        apprenant.nom || '',
        apprenant.email || '',
        apprenant.entreprise || '',
        getLastNiveau(apprenant, niveaux) || '',
        formatDateShort(getNiveauDate(niveaux, 'M+0')) || '',
        formatDateShort(getNiveauDate(niveaux, 'M+3')) || '',
        formatDateShort(getNiveauDate(niveaux, 'M+6')) || ''
      ];
    });
    const csvContent = [
      headers.join(';'),
      ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';'))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'suivi_apprenants.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Export CSV terminé !');
  };

  // Early return for loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const aContacter = getApprenantsEnAttente();

  const modalTotalPages = Math.ceil(detailsApprenants.length / modalPerPage);
  const modalPaginatedApprenants = detailsApprenants.slice(
    (modalPage - 1) * modalPerPage,
    modalPage * modalPerPage
  );

  const renderRows = () => {
    if (loadingTable) {
      return Array.from({ length: 6 }).map((_, i) => (
        <tr key={`skeleton-${i}`} className="animate-pulse">
          <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
          <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
          <td className="px-4 py-3 text-center"><div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div></td>
          <td className="px-4 py-3 text-center"><div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div></td>
          <td className="px-4 py-3 text-center"><div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div></td>
          <td className="px-4 py-3 text-center"><div className="h-8 bg-gray-200 rounded w-24 mx-auto"></div></td>
        </tr>
      ));
    }

    return paginatedApprenants.map(apprenant => {
      const niveaux = buildNiveauxFor(apprenant);
      const lastNiveau = getLastNiveau(apprenant, niveaux);
      return (
        <tr key={apprenant._id} className="hover:bg-gray-50 transition-colors duration-150">
          <td className="px-4 py-3">
            <div className="flex items-center gap-3">
              <div>
                <div className="font-medium">{apprenant.nom} {apprenant.prenom}</div>
                <div className="text-xs text-gray-500">{apprenant.email || ''}</div>
              </div>
              <div>
                {(() => {
                  const positifs = config?.statutsInsertion?.positif || [];
                  const aUnSuivi = suivis.some(s =>
                    s.apprenantId?._id === apprenant._id || s.apprenantId === apprenant._id
                  );
                  const isPositif = positifs.includes(apprenant.statutInsertion);

                  if (lastNiveau) {
                    return (
                      <span
                        title={getNiveauDate(niveaux, lastNiveau) ? `Dernier: ${formatDateShort(getNiveauDate(niveaux, lastNiveau))}` : lastNiveau}
                        className={`text-xs font-semibold px-2 py-1 rounded-full transition-colors duration-150 ${lastNiveau === 'M+6' ? 'bg-green-100 text-green-800'
                          : lastNiveau === 'M+3' ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}
                      >
                        {lastNiveau}
                      </span>
                    );
                  }

                  if (aUnSuivi && isPositif) {
                    return (
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-800 transition-colors duration-150" title="Suivi post-insertion effectué">
                        Suivi post-insertion
                      </span>
                    );
                  }

                  return (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600 transition-colors duration-150" title="Aucun suivi enregistré">
                      Non suivi
                    </span>
                  );
                })()}
              </div>
            </div>
          </td>
          <td className="px-4 py-3">{apprenant.entreprise || '-'}</td>
          <td className="px-4 py-3 text-center">
            {niveaux['M+0']
              ? <span title={formatDateShort(getNiveauDate(niveaux, 'M+0'))} className="text-xs font-medium px-2 py-1 rounded bg-blue-50 text-blue-800">{formatDateShort(getNiveauDate(niveaux, 'M+0'))}</span>
              : <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">À faire</span>}
          </td>
          <td className="px-4 py-3 text-center">
            {niveaux['M+3']
              ? <span title={formatDateShort(getNiveauDate(niveaux, 'M+3'))} className="text-xs font-medium px-2 py-1 rounded bg-amber-50 text-amber-800">{formatDateShort(getNiveauDate(niveaux, 'M+3'))}</span>
              : <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">À faire</span>}
          </td>
          <td className="px-4 py-3 text-center">
            {niveaux['M+6']
              ? <span title={formatDateShort(getNiveauDate(niveaux, 'M+6'))} className="text-xs font-medium px-2 py-1 rounded bg-green-50 text-green-800">{formatDateShort(getNiveauDate(niveaux, 'M+6'))}</span>
              : <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">À faire</span>}
          </td>
          <td className="px-4 py-3 text-center">
            {(() => {
              const positifs = config?.statutsInsertion?.positif || [];
              const isPositif = positifs.includes(apprenant.statutInsertion);

              if (!isPositif) {
                return <span className="text-xs text-gray-400 italic">Statut insertion inconnu</span>;
              }

              return (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => openMultiNiveauxModal(apprenant._id)}
                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors duration-150"
                  >
                    Voir / Suivre
                  </button>
                  <button
                    onClick={() => openSuiviModal(apprenant._id)}
                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors duration-150"
                  >
                    Nouveau suivi
                  </button>
                </div>
              );
            })()}
          </td>
        </tr>
      );
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
        <Icon name="suivi" size={28} className="mr-3 text-red-600" />
        Suivi Post-Insertion
      </h2>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 animate-fadeIn">
        <div
          className="kpi-card section-card bg-white border-l-4 border-green-500 p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer"
          onClick={() => handleKpiClick('SUIVIS_OK')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="success" size={24} color="#10b981" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Suivis effectués</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{getApprenantsAvecSuiviOK().length}</p>
        </div>
        <div
          className="kpi-card section-card bg-white border-l-4 border-amber-500 p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer"
          onClick={() => handleKpiClick('EN_ATTENTE')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="clock" size={24} color="#f59e0b" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">En attente</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{getApprenantsEnAttente().length}</p>
        </div>
        <div
          className="kpi-card section-card bg-white border-l-4 border-red-600 p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer"
          onClick={() => handleKpiClick('EN_RETARD')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="error" size={24} color="#dc2626" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">En retard</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{getApprenantsEnRetard().length}</p>
        </div>
        <div className="kpi-card section-card bg-white border-l-4 border-blue-500 p-5 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between mb-2">
            <Icon name="success" size={24} color="#3b82f6" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Note moyenne</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{suiviStats?.noteMoyenne || '-'}/5</p>
        </div>
      </div>

      {/* Modal de détails */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setModalPage(1); }}
        title={`${detailsTitle} (${detailsApprenants.length})`}
        size="xl"
      >
        <div>
          {detailsApprenants.length > 0 ? (
            <>
              <div className="max-h-96 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Nom & prénoms</th>
                      <th className="px-4 py-2 text-left">Entreprise</th>
                      <th className="px-4 py-2 text-left">Téléphone</th>
                      <th className="px-4 py-2 text-left">Formation</th>
                      <th className="px-4 py-2 text-left">Statut suivi</th>
                      <th className="px-4 py-2 text-center">Date dernier suivi</th>
                      <th className="px-4 py-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {modalPaginatedApprenants.map(apprenant => {
                      // Chercher dans la collection suivis (suivi simple)
                      const suiviSimple = suivis
                        .filter(s => s.apprenantId?._id === apprenant._id || s.apprenantId === apprenant._id)
                        .filter(s => !s.suivisMultiNiveaux?.length) // suivi simple = pas de multi-niveaux
                        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

                      // Chercher le dernier niveau dans apprenant.suivisMultiNiveaux
                      const niveaux = {};
                      (apprenant.suivisMultiNiveaux || []).forEach(n => {
                        if (!niveaux[n.niveau] || new Date(n.dateContact) > new Date(niveaux[n.niveau].dateContact)) {
                          niveaux[n.niveau] = n;
                        }
                      });
                      const dernierNiveau = apprenant.dernierNiveauSuivi
                        || (niveaux['M+6'] ? 'M+6' : niveaux['M+3'] ? 'M+3' : niveaux['M+0'] ? 'M+0' : null);

                      // Déterminer le statut et la date à afficher
                      let statutSuivi = '-';
                      let dateSuivi = '-';

                      if (dernierNiveau) {
                        // Priorité au multi-niveaux
                        statutSuivi = dernierNiveau;
                        const entry = niveaux[dernierNiveau];
                        dateSuivi = entry?.dateContact ? formatDateShort(entry.dateContact) : '-';
                      } else if (suiviSimple) {
                        // Suivi simple
                        statutSuivi = 'Suivi post-insertion';
                        dateSuivi = suiviSimple.date ? formatDateShort(suiviSimple.date) : '-';
                      }

                      return (
                        <tr key={apprenant._id} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <div className="font-medium">{apprenant.nom} {apprenant.prenom}</div>
                            <div className="text-xs text-gray-500">{apprenant.email}</div>
                          </td>
                          <td className="px-4 py-2">{apprenant.entreprise || '-'}</td>
                          <td className="px-4 py-2">{apprenant.telephone || '-'}</td>
                          <td className="px-4 py-2">{apprenant.typeFormation || '-'}</td>
                          <td className="px-4 py-2">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${dernierNiveau === 'M+6' ? 'bg-green-100 text-green-800'
                              : dernierNiveau === 'M+3' ? 'bg-amber-100 text-amber-800'
                                : dernierNiveau === 'M+0' ? 'bg-blue-100 text-blue-800'
                                  : suiviSimple ? 'bg-purple-100 text-purple-800'
                                    : 'bg-gray-100 text-gray-600'
                              }`}>
                              {statutSuivi}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">{dateSuivi}</td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => { setShowDetailsModal(false); openMultiNiveauxModal(apprenant._id); }}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              >
                                Voir / Suivre
                              </button>
                              <button
                                onClick={() => { setShowDetailsModal(false); openSuiviModal(apprenant._id); }}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                              >
                                Nouveau Suivi
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {modalTotalPages > 1 && (
                <div className="mt-4 flex justify-between items-center border-t pt-3">
                  <p className="text-sm text-gray-500">
                    {detailsApprenants.length} entrée{detailsApprenants.length > 1 ? 's' : ''}
                    {' '}— page {modalPage}/{modalTotalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setModalPage(p => Math.max(1, p - 1))}
                      disabled={modalPage === 1}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >◀</button>
                    <span className="px-3 py-1 bg-gray-100 rounded text-sm">
                      {modalPage} / {modalTotalPages}
                    </span>
                    <button
                      onClick={() => setModalPage(p => Math.min(modalTotalPages, p + 1))}
                      disabled={modalPage >= modalTotalPages}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >▶</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Aucun apprenant dans cette catégorie
            </div>
          )}
        </div>
      </Modal>
      {/* À Contacter */}
      <div className="section-card bg-white rounded-xl p-6 mb-6 transition-all duration-300">
        <h3 className="font-bold mb-4 flex items-center text-gray-800">
          <Icon name="phone" size={20} className="mr-2 text-red-600" />
          À Contacter
        </h3>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {aContacter.length > 0 ? (
            aContacter.map(apprenant => {
              const jours = Math.floor((new Date() - new Date(apprenant.dateInsertion)) / (1000 * 60 * 60 * 24));
              return (
                <div
                  key={apprenant._id}
                  className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200 hover:shadow-md hover:bg-amber-100 transition-all duration-200"
                >
                  <div>
                    <p className="font-medium">{apprenant.nom} {apprenant.prenom}</p>
                    <p className="text-sm text-gray-600">{apprenant.entreprise || '-'} | {jours}j</p>
                  </div>
                  {isFormateur && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openMultiNiveauxModal(apprenant._id)}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors duration-150"
                        title="Suivi multi-niveaux (M+0/M+3/M+6)"
                      >
                        <Icon name="liste" size={16} />
                        <span>Multiniveaux</span>
                      </button>
                      <button
                        onClick={() => openSuiviModal(apprenant._id)}
                        className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors duration-150"
                        aria-label="Contacter"
                      >
                        <Icon name="phone" size={16} />
                        <span>Contacter</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-500 py-4 flex flex-col items-center">
              <Icon name="success" size={40} className="text-green-500 mb-2" />
              <p>Aucun suivi en attente</p>
            </div>
          )}
        </div>
      </div>

      {/* Suivi par apprenant */}
      <div className="section-card bg-white rounded-xl p-6 mb-6">
        <h3 className="font-bold mb-4 flex items-center text-gray-800">
          <Icon name="liste" size={20} className="mr-2 text-red-600" />
          Suivi par apprenant
        </h3>
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">{displayedApprenants.length} apprenant(s)</div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Icon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un apprenant..."
                className="pl-9 pr-3 py-1.5 border rounded bg-white text-sm w-52 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Filtrer :</label>
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="px-2 py-1 border rounded bg-white text-sm"
              >
                <option value="ALL">Tous</option>
                <option value="M+0">M+0</option>
                <option value="M+3">M+3</option>
                <option value="M+6">M+6</option>
                <option value="NONE">Non suivi</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Trier :</label>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="px-2 py-1 border rounded bg-white text-sm"
              >
                <option value="NAME">Nom</option>
                <option value="NIVEAU_DESC">Niveau (desc)</option>
                <option value="NIVEAU_ASC">Niveau (asc)</option>
              </select>
            </div>
            <button
              onClick={exportCSV}
              className="px-3 py-1 bg-gray-100 rounded text-sm hover:bg-gray-200 transition-colors duration-150"
            >
              Exporter CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Apprenant</th>
                <th className="px-4 py-3 text-left">Entreprise</th>
                <th className="px-4 py-3 text-center">M+0</th>
                <th className="px-4 py-3 text-center">M+3</th>
                <th className="px-4 py-3 text-center">M+6</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {renderRows()}
              {paginatedApprenants.length === 0 && !loadingTable && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    {apprenants.length === 0 ? 'Aucun apprenant' : 'Aucun résultat pour ce filtre'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {displayedApprenants.length} apprenant{displayedApprenants.length > 1 ? 's' : ''}
            {' '}— page {currentPage}/{totalPages || 1}
          </p>
          <div className="flex gap-2">
            {/* <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
            >
              «
            </button> */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
            >
              ◀
            </button>
            <span className="px-3 py-1 bg-gray-100 rounded text-sm">
              {currentPage} / {totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
            >
              ▶
            </button>
            {/* <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
            >
              »
            </button> */}
          </div>
        </div>
      </div>

      {/* Historique */}
      <div className="section-card bg-white rounded-xl p-6 transition-all duration-300">
        <h3 className="font-bold mb-4 flex items-center text-gray-800">
          <Icon name="liste" size={20} className="mr-2 text-red-600" />
          Historique
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Apprenant</th>
                <th className="px-4 py-3 text-left">Entreprise</th>
                <th className="px-4 py-3 text-center">Date</th>
                <th className="px-4 py-3 text-center">Type</th>
                <th className="px-4 py-3 text-center">Note</th>
                <th className="px-4 py-3 text-left">Appréciation</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {historique.map(suivi => {
                const apprenant = typeof suivi.apprenantId === 'object'
                  ? suivi.apprenantId
                  : apprenants.find(a => a._id === suivi.apprenantId);
                return (
                  <tr key={suivi._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-4 py-3">{apprenant?.nom || '-'}</td>
                    <td className="px-4 py-3">{apprenant?.entreprise || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      {suivi.date ? format(new Date(suivi.date), 'dd/MM/yyyy') : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {suivi.type === 'telephone' ? '📞' : suivi.type === 'email' ? '📧' : '🏢'}
                    </td>
                    <td className="px-4 py-3 text-center">{'⭐'.repeat(suivi.note || 0)}</td>
                    <td className="px-4 py-3">{suivi.appreciation || '-'}</td>
                  </tr>
                );
              })}
              {historique.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">Aucun suivi</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Suivi Simple */}
      <Modal
        isOpen={showSuiviModal}
        onClose={() => setShowSuiviModal(false)}
        title={
          <span className="flex items-center">
            <Icon name="phone" size={20} className="mr-2" />
            Suivi Post-Insertion
          </span>
        }
        size="md"
      >
        {selectedApprenant && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">{selectedApprenant.nom} {selectedApprenant.prenom}</p>
              <p className="text-sm text-gray-600">{selectedApprenant.entreprise || '-'}</p>
            </div>
            <FormInput
              label="Date"
              name="date"
              type="date"
              value={suiviForm.date}
              onChange={(e) => setSuiviForm({ ...suiviForm, date: e.target.value })}
              required
            />
            <FormSelect
              label="Type contact"
              name="type"
              value={suiviForm.type}
              onChange={(e) => setSuiviForm({ ...suiviForm, type: e.target.value })}
              required
              options={[
                { value: 'telephone', label: '📞 Téléphone' },
                { value: 'email', label: '📧 Email' },
                { value: 'visite', label: '🏢 Visite' }
              ]}
            />
            <div>
              <label htmlFor="suivi-note" className="block text-sm font-medium mb-1">Note (1-5) *</label>
              <div className="flex gap-2" role="radiogroup" aria-labelledby="suivi-note-label">
                <span id="suivi-note-label" className="sr-only">Note (1-5)</span>
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => { setRating(star); setSuiviForm({ ...suiviForm, note: star }); }}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    className={`text-3xl transition-transform duration-150 ${star <= (hoveredStar || rating) ? 'text-yellow-400' : 'text-gray-300'
                      } ${star === rating ? 'scale-125' : 'hover:scale-125'
                      }`}
                    aria-label={`Noter ${star} étoile${star > 1 ? 's' : ''}`}
                  >
                    ⭐
                  </button>
                ))}
              </div>
              <input id="suivi-note" type="hidden" name="note" value={rating} required />
            </div>
            <FormSelect
              label="Appréciation"
              name="appreciation"
              value={suiviForm.appreciation}
              onChange={(e) => setSuiviForm({ ...suiviForm, appreciation: e.target.value })}
              options={[
                { value: '', label: '-' },
                { value: 'excellent', label: '🌟 Excellent' },
                { value: 'tres_bon', label: '👍 Très bon' },
                { value: 'bon', label: '👌 Bon' },
                { value: 'moyen', label: '😐 Moyen' },
                { value: 'insuffisant', label: '👎 Insuffisant' }
              ]}
            />
            <div>
              <label htmlFor="suivi-commentaire" className="block text-sm font-medium mb-1">Commentaire</label>
              <textarea
                id="suivi-commentaire"
                name="commentaire"
                value={suiviForm.commentaire}
                onChange={(e) => setSuiviForm({ ...suiviForm, commentaire: e.target.value })}
                rows="2"
                autoComplete="off"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-150"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowSuiviModal(false)}
                className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-150"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-150"
              >
                Enregistrer
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal Suivi Multi-Niveaux */}
      <ModalSuiviMultiNiveaux
        isOpen={showMultiNiveauxModal}
        onClose={() => setShowMultiNiveauxModal(false)}
        apprenant={selectedMultiNiveauApprenant}
        onSuccess={async () => {
          await fetchData();
          setShowMultiNiveauxModal(false);
        }}
      />
    </div>
  );
};

export default Suivi;